/**
 * @aw/video-util @ v1.10.0
 * 
 * Copyright © 2020 American Well.
 * All rights reserved.
 * 
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */

/*
 * American Well Web Video SDK
 *
 * Copyright © 2020 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */

/**
 * Create an object of node values, with keys corresponding to some attribute value
 *
 * @module attach
 * @example
 * import { attach } from '@aw/video-util';
 */

const defaults$1 = {
  selector: '[data-aw-id]',
  key(node) {
    return node.getAttribute('data-aw-id');
  },
};

/**
 * Attach nodes matching `opts.selector` querySelector to an object, using the value of the attribute as the key
 *
 * @method
 * @param {HTMLElement} root the root container to limit our attaching within
 * @param {object=} opts property bag of default overrides
 * @param {string} opts.selector the CSS selector used to target the elements
 * @param {Function} opts.key a function used to process a node and return a string key to use
 * @returns {object} the dictionary of `attribute: node` pairs
 */
var attach = (root, opts) => {
  const nodes = {};
  const config = { ...defaults$1, ...opts };
  Array.prototype.slice.call(root.querySelectorAll(config.selector)).forEach((node) => {
    nodes[config.key(node)] = node;
  });
  return nodes;
};

/*
 * American Well Web Video SDK
 *
 * Copyright © 2020 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */

/**
 * @module attach_event
 */

const defaults = {
  selector: '[data-aw-event]',
  key(node) {
    return node.getAttribute('data-aw-event');
  },
};

/**
 * Scan dom nodes for special decoration, and attach an event name to a contextual function.
 *
 * eg: `<button data-aw-event='onclick: handleClick'>` will attach the button's `onclick` event to
 * a passed context's `handleClick` method
 *
 * Only one event name can exist per node, though multiple events can be defined in a single
 * attribute by separating the pairs with a comma, eg:
 *
 * ```
 * <p data-aw-event='onclick: handler, onmouseover: onMouse'>Hello, World</p>
 * ```
 *
 * @method
 * @param {HTMLElement} root the root dom node to scan
 * @param {object} context the instance to use for attaching events
 * @param {object} [opts] an object to mix over default parameters
 * @param {string} [opts.selector] the selector to use to find elements, defaults to `data-aw-event`
 * @param {Function} [opts.key] a function that is passed a matching node element to return the value used in event-attaching
 * @returns {undefined} nothing
 */
var attach_event = (root, context, opts) => {
  const config = { ...defaults, ...opts };
  Array.prototype.slice.call(root.querySelectorAll(config.selector)).forEach((node) => {
    const attr = config.key(node);
    attr.split(',').forEach((match) => {
      const parts = match.split(':');
      const event = parts[0].trim();
      const handler = parts[1].trim();
      node[event] = context[handler].bind(context);
    });
  });
};

/*
 * American Well Web Video SDK
 *
 * Copyright © 2020 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */

/**
 * @module by_id
 */

/**
  * Shorthand for `document.getElementById`, allowing Node reference passthrough
  *
  * @method
  * @param {string|DomNode} id the id or a reference to a domNode
  * @returns {DomNode} if found
  */
var byId = (id) => (typeof id === 'string' ? document.getElementById(id) : id);

/**
 * Set an attribute value on a node, or set a batch of attributes at once as a shorthand.
 *
 * @param {String|DomNode} node the node to set attribute values on
 * @param {String|Object} key The key name to set, or a hash of k:v pairs to set in bulk
 * @param {any?} value the value to assign
 * @returns {undefined} nothing
 */
const attr = (node, key, value) => {
  const n = byId(node);
  if (typeof key === 'string') {
    n.setAttribute(key, value);
  } else {
    Object.keys(key).forEach((prop) => {
      n.setAttribute(prop, key[prop]);
    });
  }
};

/*
 * American Well Web Video SDK
 *
 * Copyright © 2020 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */

/**
 * Toggle classes on a node
 *
 * @method
 * @param {DomNode|string} node the node or string ID of a node to manipulate
 * @param  {...any} args the additional args to be passed to the classList method
 * @returns {*} whatever the classlist method would return
 */
const toggleClass = (node, ...args) => byId(node).classList.toggle(...args);

/**
 * Add classes to a node
 *
 * @method
 * @param {DomNode|string} node the node or string ID of a node to manipulate
 * @param  {...any} args the additional args to be passed to the classList method
 * @returns {*} whatever the classlist method would return
 */
const addClass = (node, ...args) => byId(node).classList.add(...args);

/**
 * Remove classes from a node
 *
 * @method
 * @param {DomNode|string} node the node or string ID of a node to manipulate
 * @param  {...any} args the additional args to be passed to the classList method
 * @returns {*} whatever the classlist method would return
 */
const removeClass = (node, ...args) => byId(node).classList.remove(...args);

/**
 * Check the presence of a class on a node
 *
 * @method
 * @param {DomNode|string} node the node or string ID of a node to manipulate
 * @param  {...any} args the additional args to be passed to the classList method
 * @returns {boolean} true if the classList contains the passed class
 */
const contains = (node, ...args) => byId(node).classList.contains(...args);

var class_list = {
  toggleClass,
  addClass,
  removeClass,
  contains,
};

/*
 * American Well Web Video SDK
 *
 * Copyright © 2020 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */

/**
 * @module create_dom

 */

/**
  * Build up a bit of real DOM
  *
  * @param {string} html a blob of markup to convert to actual DOM
  * @example
  * import createDom from '@aw/video-util/dist/create_dom';
  * @example
  * const dom = createDom('<p class="foo">Hello, World</p>');
  * document.body.appendChild(dom);
  */
var create_dom = (html) => document.createRange().createContextualFragment(html);

/**
 * Generate a function that will only execute once for sequential calls within
 * a timeframe.
 *
 * @example
 * const fn = debounce(() => {
 *   console.warn('Hello, World.');
 * }, 1000);
 *
 * // you should only see `hello, world` 1000ms after the last resize event was fired
 * window.addEventListener('resize', false, fn);
 *
 * @param {Function} cb a callback function to execute
 * @param {Number} delay time in MS to wait before executing callback.
 * @returns {Function} the debounced function
 */
const debounce = (cb, delay) => {
  let timer;
  return function debounced(...args) {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      cb(...args);
    }, delay);
  };
};

const __deprecationCache = {};

/**
   * Log a debug message to the console to indicate a behavior has been deprecated.
   *
   * @param {string} behavior The API or behavior being deprecated. Usually in the form of "myApp.someFunction()
   * @param {string=} extra Text to append to the message. Often provides advice on a new function or facility to achieve the same goal during the deprecation period.
   * @param {string=} removal Text to indicate when in the future the behavior will be removed. Usually a version number.
   * @returns {undefined} nothing.
   */
const deprecated = (behavior, extra, removal) => {
  const message = ['DEPRECATED:', behavior];
  if (extra) { message.push(extra); }
  if (removal) { message.push('-- will be removed in version:', removal); }
  const out = message.join(' ');
  if (!__deprecationCache[out]) {
    __deprecationCache[out] = 1;
    // eslint-disable-next-line no-console
    console.warn(out);
  }
};

/*
 * American Well Web Video SDK
 *
 * Copyright © 2020 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */

/**
 * Store and update a preferred device list from localStorage
 *
 * @module preferred_devices
 * @example
 * import { devices } from '@aw/video-util';
 * const preferred = devices.get();
 */

/**
 * Save an object to the currentDevices localStorage
 *
 * @private
 * @param {object} obj the key/value properties to write to localstorage
 * @returns {undefined} whatever localStorage.setItem would
 */
const save = (obj) => localStorage.setItem('AWLCurrentDevices', JSON.stringify(obj));

/**
 * Get the current stored preferred devices
 *
 * @returns {object} a property bag that may or may not have values previously set
 */
const get$2 = () => {
  const currentDevicesJSON = localStorage.getItem('AWLCurrentDevices');
  return (currentDevicesJSON && JSON.parse(currentDevicesJSON)) || {};
};

/**
 * Add a single device to the preferred devices stored
 *
 * @param {string} type the type of device to set
 * @param {string} id the deviceId of the device
 * @param {string} name The display name of the device
 * @returns {undefined} nothing
 */
const set = (type, id, name) => {
  const currentDevices = get$2();
  currentDevices[`${type}DeviceId`] = id;
  currentDevices[`${type}DeviceName`] = name;
  currentDevices[`${type}DeviceLabel`] = name;
  save(currentDevices);
};

/**
 * Mix a partial (or full) updated device list into the preferred
 * storage list.  Duplicate keys will be overwritten by the passed obj,
 * and existing keys will be preserved
 *
 * @param {object} obj the partial device list to mix over the current devices
 * @returns {object} the resulting mixed device list
 */
const mix = (obj) => {
  const mixed = Object.assign(get$2(), obj);
  save(mixed);
  return mixed;
};

/**
 */
var preferred_devices = {
  get: get$2,
  set,
  mix,
};

/*
 * American Well Web Video SDK
 *
 * Copyright © 2020 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */

/**
 * @module empty
 */

/**
 * Safely empty the contents of a domNode
 *
 * @method
 * @param {string|DomNode} node The node to empty, or a string ID of the node
 * @returns {undefined} undefined
 * @example
 * import { empty } from '@aw/video-util';
 * empty(document.body);
 */
var empty = (node) => {
  const domNode = typeof node === 'string' ? document.getElementById(node) : node;
  // eslint-disable-next-line no-cond-assign
  for (let c; c = domNode.lastChild;) { // intentional assignment
    domNode.removeChild(c);
  }
};

/*
 * American Well Web Video SDK
 *
 * Copyright © 2020 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */

/**
  * Converts HTML syntax in strings to their charref values
  *
  * @module escape_html
  * @example
  * import { escape_html } from '@aw/video-util';
  * console.log(escape_html('<img src=escape.com/html>'));
  */

/**
 * Escape HTML markup within a string
 *
 * @method
 * @returns {String} String with HTML markup converted to charref values
 */

const map = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
};

var escape_html = (str) => ((str || '').replace(/[&<>"]/g, (s) => map[s]));

/*
 * American Well Web Video SDK
 *
 * Copyright © 2020 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */

/**
  * Connect some `fn` to an `el`'s `event`
  *
  * @method
  * @param {HTMLElement} el The element to base the connection on
  * @param {string} event The name of the event, without the 'on'
  * @param {Function} fn The event handler to use
  * @returns {object} A `handle` that can be passed to `disconnect` to easily remove the event listener.
  * @example
  * import { connect, disconnect } from '@aw/video-util/dist/event';
  * const handle = connect(document.querySelector('#foo'), "click", () => {
  *     disconnect(handle);
  * });
  */
const connect = (el, event, fn) => {
  el.addEventListener(event, fn, false);
  return { el, event, fn };
};

/**
 * Disconnect some event from element, specifically the event attached by using `connect`
 *
 * @method
 * @param {object} handle The return value from a `connect` call, used to lookup the event listener to remove
 * @returns {undefined} nothing.
 */
const disconnect = (handle) => {
  const { el, event, fn } = handle;
  el.removeEventListener(event, fn);
};

var event = {
  connect,
  disconnect,
};

/**
 * @module event
 */

/*
 * American Well Web Video SDK
 *
 * Copyright © 2020 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */

/**
 * @module format_date
 */

/**
 * Format a date as short ISO string
 *
 * @method
 * @param {Date} date the date to format
 */
var format_date = (date) => {
  if (!(date instanceof Date)) {
    return undefined;
  }
  return date.toISOString().split('T')[0];
};

/*
 * American Well Web Video SDK
 *
 * Copyright © 2020 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */

/**
 * @module fullscreen
 */

let prefix; // the vendor prefix we'll use for the event
let method; // the full method name once detected
let found; // whether or not we found an `opener` function

// all the [supported] combinations of prefix / methodName
const openers = [
  ':requestFullscreen',
  'moz:RequestFullScreen',
  'webkit:RequestFullScreen',
];

/*
 * A feature detection test to help determine which
 * event name is fired when the browser changes fullscreen state
 */
const eventName = (() => {
  const node = document.createElement('div');
  found = openers.some((name) => {
    const [pre, meth] = name.split(':');
    prefix = pre;
    method = [pre + meth];
    return typeof node[method] === 'function';
  });
  return `${prefix}fullscreenchange`;
})();

/**
 * Request fullscreen mode of a `node` in a cross browser way
 *
 * @param {HTMLElement} node the node to make fullscreen
 * @returns {boolean} if no method found or executed, this is false. Otherwise true.
 */
const on = (node) => found && node[method]() && true;

/**
 * The correct event name to use when listening for fullscreenchange
 *
 * @memberof fullscreen
 * @type {string}
 */
on.CHANGE = eventName;

/**
 * Minimize a browser fullscreen mode
 *
 * @method
 * @memberof fullscreen
 * @returns {undefined} nothing
 */
on.off = () => {
  if (document.exitFullscreen) {
    document.exitFullscreen();
  } else if (document.webkitIsFullScreen) {
    document.webkitExitFullscreen();
  } else if (document.mozFullScreen) {
    document.mozCancelFullScreen();
  }
};

/**
 * Check to see if the browser is currently fullscreen, as cross-browser as humanly possible.
 *
 * @method
 * @memberof fullscreen
 * @returns {boolean} true if fullscreen enabled, false otherwise.
 */
on.isOn = () => (
  !!(document.fullScreen
  || document.mozFullScreen
  || document.webkitIsFullScreen
  || document.webkitCurrentFullScreenElement
  || document.fullscreenElement
  || (window.outerHeight - window.innerHeight <= 1))
);

let audioContextSingleton;

/**
 * Wrapper around fetching a single `AudioContext` instance.
 *
 * @returns {AudioContext} a single shared AudioContext instance
 */
const getAudioContext = () => {
  if (!audioContextSingleton) {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    audioContextSingleton = new AudioContext();
  }
  return audioContextSingleton;
};

/*
 * American Well Web Video SDK
 *
 * Copyright © 2020 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */

/**
 * @module get_messsages
 */

/**
 * Resolve the passed in locale string to one that common supports and return the associated messages dictionary.
 * Will default to 'en' if locale is not defined.
 *
 * @method
 * @param {object} bundle a bundle of i18n strings, with locale as the key
 * @param {string} locale the locale string to be resolved
 * @returns {object} the messages dictionary object
 */
var get_messages = (bundle, locale = 'en') => {
  const messages = bundle || {};
  const [base] = locale.split(/_|-/);
  return messages[locale] || messages[base] || messages.en || {};
};

/*
 * American Well Web Video SDK
 *
 * Copyright © 2020 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */

/**
 * @module get_object
 */

/**
  * Get a property from a list of `parts`
  * @private
  * @param {*} parts
  * @param {boolean} create create objects along path
  * @param {*} context
  * @returns {*} the property found or undefined
  */
const getProp = (parts, create, context) => {
  let root = context || window;
  let p;
  for (let i = 0; i < parts.length; i += 1) {
    p = parts[i].trim();
    try {
      if (!(p in root)) {
        if (create) {
          root[p] = {};
        } else {
          return undefined;
        }
      }
      root = root[p];
    } catch (e) {
      return undefined;
    }
  }
  return root;
};

/**
 * Get a property from a dot-separated string, such as "A.B.C"
 *
 * @method
 * @param {string} name The dot-separated string to search for in `context`
 * @param {boolean} [create] toggle to create objects at intermediate name locations
 * @param {*} [context] The root context to begin searching. Defaults to `window`
 * @returns {*} Whatever the property found in `name` was, or undefined.
 * @example
 * import getObject from '@aw/video-util/dist/get_object';
 * @example
 * import { getObject } from '@aw/video-util';
 * @example
 * const o = { a: { b: 42 } };
 * const v = getObject("a.b", false, o); // 42
 * @example
 * const o = {};
 * const v = getObject("a.b.c.d.e.f.g", true, o);
 * console.log(v); // { a: { b:{ c:{ d:{ e:{ f:{ g:{} } } } } } } }
 */
var getObject = (name, create, context) => {
  const parts = name.split('.');
  return getProp(parts, create, context);
};

/*
 * American Well Web Video SDK
 *
 * Copyright © 2020 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */

/**
 * @module get_track_settings
 */

/**
 * Get detailed information about a MediaStreams attached audio/video tracks
 *
 * @method
 * @param {MediaStream} stream a stream to extract track details from
 * @param {string} kind pass 'audio' to get audio track settings, or 'video' for video track settings
 * @returns {object|boolean} the settings extracted from the stream, or `false` if no tracks available.
 */
var getDevice = (stream, kind) => {
  const method = kind === 'audio' ? 'getAudioTracks' : 'getVideoTracks';
  const tracks = stream[method] && stream[method]();
  return tracks && tracks.length && tracks[0] && tracks[0].getSettings && tracks[0].getSettings();
};

/*
 * American Well Web Video SDK
 *
 * Copyright © 2021 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */

/**
  * check the userAgent to determine if on a chrome web browser or not
  *
  * @module chrome_browser_detect
  * @example
  * import { isChrome } from '@aw/video-util';
  * console.log('webrowser is chrome === ', isChrome());
  */

/**
 * Determine if we're currently in a chrome browser
 *
 * @method
 * @returns {boolean} true if we're a chrome browser
 */
var chrome_browser_detect = () => navigator.userAgent.indexOf('Chrome') !== -1;

/*
 * American Well Web Video SDK
 *
 * Copyright © 2020 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */

/**
  * Simple check to determine if we are running in an Electron environment
  *
  * @module electron_detect
  * @example
  * import { isElectron } from '@aw/video-util';
  * console.log('user is Electron === ', isElectron());
  */

/**
 * Determine if we're currently in an Electron environment
 *
 * @method
 * @returns {boolean} true if we're an Electron environment
 */
var electron_detect = () => {
  const userAgent = (navigator.userAgent || navigator.vendor || window.opera);
  return userAgent.toLowerCase().indexOf(' electron/') > -1;
};

/*
 * American Well Web Video SDK
 *
 * Copyright © 2020 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */

/**
  * Comprehensive regex check of the userAgent to determine if on a mobile browser or not
  *
  * @module mobile_browser_detect
  * @example
  * import { isMobile } from '@aw/video-util';
  * console.log('user is mobile === ', isMobile());
  */

// eslint-disable-next-line no-useless-escape
const absurdPrefixRegex = /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i;
// eslint-disable-next-line no-useless-escape
const absurdMobileRegex = /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i;

/**
 * Determine if we're currently in a mobile browser
 *
 * @method
 * @returns {boolean} true if we're a mobile browser
 */
var mobile_browser_detect = () => {
  const userAgent = (navigator.userAgent || navigator.vendor || window.opera);
  return absurdPrefixRegex.test(userAgent.substr(0, 4)) || absurdMobileRegex.test(userAgent);
};

/*
 * American Well Web Video SDK
 *
 * Copyright © 2023 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */

/**
  * check the userAgent to determine if on a Safari web browser or not
  *
  * @module Safari_browser_detect
  * @example
  * import { isSafari } from '@aw/video-util';
  * console.log('webrowser is safari === ', isSafari());
  */

/**
 * Determine if we're currently in a safari browser
 *
 * @method
 * @returns {boolean} true if we're a safari browser
 */
var safari_browser_detect = () => /^(?!.*chrome).*safari/i.test(navigator.userAgent);

/**
 * Determine if a device appears to be a touchable thing.
 *
 * @returns {Boolean} true if a device appears to be touchable for interaction
 */
const isTouchable = () => !!(navigator.maxTouchPoints && navigator.maxTouchPoints > 2);

const touchableMac = () => !!(navigator.userAgent.match(/(iPad)/) || (navigator.platform === 'MacIntel' && isTouchable()));

/*
 * American Well Web Video SDK
 *
 * Copyright © 2020 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */

/**
 * Determines whether an element is visible in the DOM.
 *
 * @method
 * @param {string|DomNode} node The node to check, or a string ID of the node.
 * @return {boolean} True if the element is visible, false if not.
 * @example
 * import { isVisible } from '@aw/video-util';
 * isVisible('#element');
 * isVisible(nodeElement);
 */
var is_visible = (node) => !(window.getComputedStyle(byId(node)).display === 'none');

/*
 * American Well Web Video SDK
 *
 * Copyright © 2020 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */

/**
 * @module load_script
 */

const head = document.head || document.getElementsByTagName('head')[0];
const re = /complete|loaded/;

/**
 *
 * Fetch a script from the interwebs asynchronously
 *
 * @method
 * @param {string} url The url for the script needing injected
 * @returns {Promise} A promise that resolves or rejects (if possible)
 * @example
 * import { loadScript } from '@aw/video-util';
 * @example
 * import load from '@aw/video-util/dist/load_script';
 * @example
 * load('//example.com/static/foo.js').then(() => {
 *   console.log('foo.js has been loaded');
 * });
 */
var load_script = (url) => new Promise((resolve, reject) => {
  const script = document.createElement('script');
  const event = script.readyState ? 'onreadystatechange' : 'onload';
  script.async = true;
  script.src = url;

  script[event] = (e) => {
    if (e.type === 'load' || re.test(script.readyState)) {
      script[event] = null;
      script.onerror = null;
      resolve(url);
    }
  };

  script.onerror = () => {
    script.onerror = null;
    script[event] = null;
    const error = new Error(`Unable to load ${url}`);
    error.name = 'ScriptFetchError';
    reject(error);
  };

  head.appendChild(script);
});

/* eslint-disable no-multi-assign */

/*
 * American Well Web Video SDK
 *
 * Copyright © 2020 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */

/**
 * Mark a moment in time as en event that is worthy of marking, and log it to the server.
 *
 * @module performance
 * @example
 * import { mark } from '@aw/video-util';
 * mark('pageLoad');
 * setTimeout(() => mark('nextLoad'), 1000);
 */

let firstMark;

/**
 *
 *
 * @method
 * @param {string} key the event name to begin timing
 * @param {boolean} dump report the metrics at this point
 * @param {...object} rest any additional parameters intended to be logged.
 * @returns {undefined} nothing
 */
const mark = (key, dump, ...rest) => {
  try {
    performance.mark(key); // does not deduplicate
    if (!firstMark) {
      // assume first call is the root/start reference point
      firstMark = key;
    }

    if (dump && mark.logger) {
      const m = performance.measure(null, firstMark, key);
      mark.logger(`PerformanceEvent - ${key} - ${m.duration} ms`, ...rest);
    }
  } catch (e) {
    /* snarf errors from performance metric monitoring, we don't want to break anything */
  }
};

mark.reset = () => {
  firstMark = null;
};

/*
 * American Well Web Video SDK
 *
 * Copyright © 2021 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */

/**
  * Check the userAgent to determine if user on a supported device or not.
  *
  * @module matchUA
  * @example
  * import { matchUA } from '@aw/video-util';
  *
  * let unsupportedDevicesList = ['SM-A32', 'SM-A12'];
  * console.log('is unsupported device === ', matchUA(unsupportedDevicesList));
  */

/**
 * Determine if user is on a supported device or not.
 *
 * @method
 * @returns {boolean} true if user's device matches a device on the given list.
 */
const matchUA = (deviceList) => {
  if (!deviceList) {
    return false;
  }
  return deviceList.some((device) => navigator.userAgent.indexOf(device) !== -1);
};

/*
 * American Well Web Video SDK
 *
 * Copyright © 2020 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */

/**
 * Safety wrapper around accessing `navigator.mediaDevices` methods
 *
 * @property {function} enumerateDevices
 * @property {function} getUserMedia
 * @property {object} actual a deferred wrapper providing access to the _actual_ mediaDevices object
 *
 * @module media_devices
 *
 * @example
 * import { mediaDevices } from '@aw/video-util';
 * mediaDevices.enumerateDevices().then(devices => console.log(devices));
 */

const reject = (msg) => Promise.reject(new Error(msg));

const polyfill = {};

['getUserMedia', 'enumerateDevices', 'getDisplayMedia'].forEach((meth) => {
  polyfill[meth] = (...args) => (navigator.mediaDevices && navigator.mediaDevices[meth](...args)) || reject(`Unable to access mediaDevices.${meth}`);
});

// Give proper access to the raw `mediaDevices` object, as some features require the actual instance rather
// than a delegate around.
Object.defineProperty(polyfill, 'actual', {
  get: () => navigator.mediaDevices,
});

/*
 * American Well Web Video SDK
 *
 * Copyright © 2020 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */

/**
 * @module observable
 */

/**
 * @typedef {object} ObservableObject
 * @property {Function} set sets key/value pairs, or a hash of key/value pairs
 * @property {Function} get get a value for a passed key
 * @property {Function} inc increment a key's numeric value. Starts with 0 is key is undefined.
 * @property {Function} onchange a callback fired when a key changes. Is passed `key, value, oldValue`
 * @property {Function} onadd a callback fired when a key is added. Is passed `key, value`
 */
const enumerable = false;
const writable = true;

/**
 * Create a new observable object.
 *
 * @method
 * @param {object} start an object defining an initial state
 * @returns {ObservableObject} the object for this observable instance
 * @example
 * import { observable } from '@aw/video-util';
 * @example
 * const x = observable({ foo: 10 });
 * x.onchange = (changes) => { console.log('changes' ,changes); };
 * x.foo += 10;
 */
var observable = (start) => {
  // ourself:
  const obj = {};

  // where we store our actual values
  const proxy = {};

  // a queue of pending changes to dispatch
  const pendingChanges = {};

  // debouncer to queue up the pending changes, interrupted by newer changes
  let timer;
  const checkQueue = () => {
    if (timer) { clearTimeout(timer); }
    timer = setTimeout(() => {
      const keys = Object.keys(pendingChanges);
      if (keys.length === 1) {
        const single = pendingChanges[keys[0]];
        obj.onchange(keys[0], single[0], single[1]);
      } else if (keys.length) {
        obj.onchange(keys, JSON.parse(JSON.stringify(pendingChanges)));
      }
      keys.forEach((key) => { delete pendingChanges[key]; });
    }, 25);
  };

  // private version of a setter that monitors for changes or additions, and relays
  // to appropriate callback/queue
  const set = (key, value) => {
    if (typeof obj.onchange === 'function' && key in proxy && proxy[key] !== value) {
      const existing = pendingChanges[key];
      pendingChanges[key] = [value, (existing ? existing[1] : proxy[key])];
    } else if (typeof obj.onadd === 'function' && !(key in proxy)) {
      obj.onadd(key, value);
    }
    proxy[key] = value;

    if (typeof obj.onchange === 'function') {
      checkQueue();
    }
  };

  // go through defineProperty for our magic functions so they are not enumerable
  Object.defineProperties(obj, {
    // standard getter function, plucks directly from proxy object
    get: {
      enumerable,
      writable: false,
      value: (k) => proxy[k],
    },
    // standard setter function, handling set(k, v) and set({ k: v })
    set: {
      enumerable,
      configurable: false,
      writable: false,
      value: (k, v) => {
        if (typeof k === 'string') {
          return obj.__set(k, v);
        }
        return Object.keys(k).map((key) => obj.__set(key, k[key]));
      },
    },
    __set: {
      enumerable,
      value: (k, v) => {
        if (!(k in proxy)) {
          // new property, define the interface to proxy through our self object
          set(k, v);
          Object.defineProperty(obj, k, {
            configurable: true,
            enumerable: true,
            set(innerValue) {
              set(k, innerValue);
            },
            get() {
              return proxy[k];
            },
          });
        } else {
          set(k, v);
        }
      },
    },
    onchange: {
      enumerable,
      writable,
    },
    onadd: {
      enumerable,
      writable,
    },
    inc: {
      value: (k, i = 1) => obj.__set(k, (obj[k] || 0) + i),
      enumerable,
    },
  });

  // define properties for anything passed in the constructor
  obj.set(start || {});

  return obj;
};

/*
 * American Well Web Video SDK
 *
 * Copyright © 2020 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */

/**
 * @module parse_version
 */

/**
 * Parse a semver version string into distinct parts.
 *
 * Provides an object, with keys for `major`, `minor`, and `patch` integers.
 * Additionally, if version string is an `alpha` release (anything with a hyphenated
 * final part), the object will contain keys containing the `dist` string, and a `snapshot` integer.
 *
 * You can mutate the object to increment version numbers directly.
 *
 * @method
 * @param {string} str the version string to parse
 * @returns {object} an object with individual parts of the version, that can be cast as a string back to the full version.
 * @example
 * import { parseVersion } from '@aw/video-util';
 * @example
 * // basic parsing
 * const x = parseVersion("1.2.3");
 * x.major === 1;
 * x.minor === 2;
 * x.patch === 3;
 * x.toString() === "1.2.3";
 * @example
 * const x = parseVersion('1.2.3-alpha.7');
 * console.warn(x); // { major: 1, minor: 2, patch: 3, dist: "alpha", snapshot: 7 }
 */
var parseVersion = (str) => {
  const p = str.split('-');
  const [major, minor, patch] = p.shift().split('.').map((i) => parseInt(i, 10));
  const dist = p.length ? p.join('-').split('.') : null;
  const o = { major, minor, patch };
  if (dist) {
    o.snapshot = parseInt(dist.pop(), 10);
    o.dist = dist.join('');
  }
  o.toString = () => [[o.major, o.minor, o.patch].join('.'), o.dist ? [o.dist, o.snapshot].join('.') : ''].join(o.dist ? '-' : '');
  return o;
};

/*
 * American Well Web Video SDK
 *
 * Copyright © 2020 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */

// default match pattern
const _pattern = /\{([^}]+)\}/g;

/**
 * Replace the tokens
 *
 * @param {string} template The source template to match against
 * @param {*} map A function to process matches individually, or a dictionary/array to use as lookup for matches
 * @param {RegEx} [pattern] A pattern-matching regular expression, which defaults to matching `{map.key}`
 * @returns {string} the replaced template
 */
var replace = (template, map, pattern) => {
  const mapper = typeof map === 'function'
    ? map
    : (_, k) => getObject(k, false, map);
  return template.replace(pattern || _pattern, mapper);
};

/*
 * American Well Web Video SDK
 *
 * Copyright © 2020 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */

/**
 * Determine if WebRTC is supported in this browser
 *
 * @module rtc_supported
 * @example
 * import { rtcSupported } from '@aw/video-util';
 * if (rtcSupported()) {
 *    console.warn("you can use WebRTC, probably.");
 * }
 */

/**
 * Looks for webRTC objects in browser window object. Returns true if window object has any of those objects. Indicating
 * support for WebRTC.
 *
 * @method
 * @returns {boolean} true if WebRTC is supported by the browser, false otherwise.
 */
var rtc_supported = () => [
  'RTCPeerConnection',
  'webkitRTCPeerConnection',
  'mozRTCPeerConnection',
  'RTCIceGatherer',
].some((item) => item in window);

/*
 * American Well Web Video SDK
 *
 * Copyright © 2021 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */

/**
  * Check the userAgent to determine if user is on an unsupported series A device.
  *
  * @module seriesADetect
  * @example
  * import { seriesADetect } from '@aw/video-util';
  *
  * console.log('is unsupported device === ', seriesADetect());
  */

/**
 * Determine if user is on an unsupported series A device.
 *
 * @method
 * @returns {boolean} true if user's device is an unsupported series A device.
 */

const unsupportedDevicesList = ['SM-A32', 'SM-A12'];

const seriesADetect = () => matchUA(unsupportedDevicesList);

/*
 * American Well Web Video SDK
 *
 * Copyright © 2020 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */

/**
 * Sets the focus on a DOM element.
 * The element needs to be focusable, otherwise it won't work.
 *
 * @method
 * @param {string|DomNode} node The node to focus, or a string ID of the node.
 * @return {undefined} undefined
 * @example
 * import { setFocus } from '@aw/video-util';
 * setFocus('#element');
 * setFocus(nodeElement);
 */
var set_focus = (node) => byId(node).focus();

/*
 * American Well Web Video SDK
 *
 * Copyright © 2020 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */

const gum$1 = polyfill.getUserMedia;
const get$1 = gum$1.bind(polyfill);
// eslint-disable-next-line no-console
const debug$1 = (...args) => console.debug('{testMedia}', ...args);

const testVideo$1 = (stream) => {
  debug$1('verifying stream', stream);
  const v = document.createElement('video');
  v.poster = '';
  v.srcObject = stream;
  v.pause();
  stream.getTracks().forEach((t) => t.stop());
  v.srcObject = null;
};

/**
 * Run a series of `getUserMedia` calls to attempt to obtain a workable MediaStream.
 *
 * @param {string} [forceCameraId] a custom deviceId to utilize when selecting the camera
 * @param {string} [forceMicId] a custom deviceId to utilize when selecting the microphone
 * @returns {Promise} A Promise which resolves to an object containing "working" devices for `audio` and `video` stream components, or rejects with an object of the error(s) encountered
 */
var test_media = (forceCameraId, forceMicId) => new Promise((resolve, reject) => {
  const obj = {};
  const audio = { audio: forceMicId ? { exact: forceMicId } : true };
  const video = { video: forceCameraId ? { exact: forceCameraId } : true };

  debug$1('GUM audio:', audio, 'video:', video);
  get$1({ audio, video })
    // allegedly both worked:
    .then((stream) => {
      obj.audio = getDevice(stream, 'audio');
      obj.video = getDevice(stream, 'video');
      testVideo$1(stream);
      obj.stream = stream;
    })

    .catch(() => {
      // check audio standalone
      debug$1('GUM audio:', audio);
      const runAudio = get$1({ audio })
        .then((stream) => {
          obj.audio = getDevice(stream, 'audio');
          testVideo$1(stream);
          obj.audiostream = stream;
        })
        .catch((e) => {
          obj.audio = e;
        });

      debug$1('GUM video:', video);
      const runVideo = get$1({ video })
        .then((stream) => {
          obj.video = getDevice(stream, 'video');
          testVideo$1(stream);
          obj.videostream = stream;
        })
        .catch((e) => {
          obj.video = e;
        });

      return Promise.all([runAudio, runVideo]);
    })

    //
    .then(() => {
      debug$1('completing:', obj);
      if (obj.video instanceof Error || obj.audio instanceof Error) {
        reject(obj);
      } else {
        resolve(obj);
      }
    });
});

/*
 * American Well Web Video SDK
 *
 * Copyright © 2020 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */

/**
 * Safely test the presence of a working `storage` implementation
 *
 * @module test_storage
 * @example
 * import { testStorage } from '@aw/video-util';
 * const canUseStorage = testStorage();
 */

/**
 * Safely test the presence of a working storage implementation
 *
 * @param {string} [adapter=sessionStorage] the name of the storage type, hosted on the `window` object
 * @returns {boolean} wether or not the storage type exists and appears working
 */
var test_storage = (adapter = 'sessionStorage') => {
  // poor man uuid for temporary use:
  const test = (Math.random() * 1e6).toString(26);

  try {
    const store = window[adapter];

    // adding an item works
    store.setItem(test, test);
    if (test !== store.getItem(test)) {
      throw new Error();
    }

    // removing an item works:
    store.removeItem(test);
    if (test === store.getItem(test)) {
      throw new Error();
    }

    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Create a function that will only execute once per `wait` ms, blocking
 * subsequent calls until the throttle threshold has passed.
 *
 * This will execute first, then throttle subsequent calls, as opposed to `debounce`
 * which will queue up sequential calls and fire _after_ the threshold.
 *
 * @example
 * const fn = throttle(() => {
 *   console.log('Hello, World.');
 * }, 1000);
 *
 * // you should see two `Hello, World.` log messages.  One immediately, and one after 1000ms
 * fn(); fn(); fn(); fn();
 * setTimeout(() => fn(), 1001);
 *
 * @param {Function} cb the function to throttle
 * @param {Number} wait the time in ms to delay before allowing cb to execute again
 * @returns {Function} the throttled function
 */

const throttle = (cb, wait) => {
  let canRun = true;
  return function throttled(...args) {
    if (!canRun) return;
    canRun = false;
    cb(...args);
    setTimeout(() => {
      canRun = true;
    }, wait);
  };
};

function uuid() {
  return performance.now().toString(36);
}

const value = (obj, key) => {
  if (typeof obj[key] === 'function') {
    return obj[key]();
  }
  return obj[key];
};

/*
 * American Well Web Video SDK
 *
 * Copyright © 2020 American Well.
 * All rights reserved.
 *
 * It is illegal to use, reproduce or distribute
 * any part of this Intellectual Property without
 * prior written authorization from American Well.
 */

const gum = polyfill.getUserMedia;
const get = gum.bind(polyfill);
// eslint-disable-next-line no-console
const debug = (...args) => console.debug('{verifyMedia}', ...args);

const testVideo = (stream) => {
  debug('verifying stream', stream);
  const v = document.createElement('video');
  v.poster = '';
  v.srcObject = stream;
  v.pause();
  v.srcObject = null;
};

/**
 * Run a series of `getUserMedia` calls to attempt to obtain a workable MediaStream.
 *
 * @param {string} [forceCameraId] a custom deviceId to utilize when selecting the camera
 * @param {string} [forceMicId] a custom deviceId to utilize when selecting the microphone
 * @returns {Promise} A Promise which resolves to an object containing "working" devices for `audio` and `video` stream components, or rejects with an object of the error(s) encountered
 */
var verify_media = (forceCameraId, forceMicId) => new Promise((resolve, reject) => {
  const obj = {};
  const audio = forceMicId ? { deviceId: { exact: forceMicId } } : true;
  const video = forceCameraId ? { deviceId: { exact: forceCameraId } } : true;

  debug('GUM audio:', audio, 'video:', video);
  get({ audio, video })
    // allegedly both worked:
    .then((stream) => {
      obj.audio = getDevice(stream, 'audio');
      obj.video = getDevice(stream, 'video');
      testVideo(stream);
      obj.stream = stream;
    })

    .catch(() => {
      // check audio standalone
      debug('GUM audio:', audio);
      const runAudio = get({ audio })
        .then((stream) => {
          obj.audio = getDevice(stream, 'audio');
          testVideo(stream);
          obj.audiostream = stream;
        })
        .catch((e) => {
          obj.audio = e;
        });

      debug('GUM video:', video);
      const runVideo = get({ video })
        .then((stream) => {
          obj.video = getDevice(stream, 'video');
          testVideo(stream);
          obj.videostream = stream;
        })
        .catch((e) => {
          obj.video = e;
        });

      return Promise.all([runAudio, runVideo]);
    })

    //
    .then(() => {
      debug('completing:', obj);
      if (obj.video instanceof Error || obj.audio instanceof Error) {
        reject(obj);
      } else {
        resolve(obj);
      }
    });
});

const version = parseVersion('1.10.0');

export { attach, attach_event as attachEvent, attr, byId, class_list as classList, create_dom as createDom, debounce, deprecated, preferred_devices as devices, empty, escape_html as escapeHtml, event, format_date as formatDate, on as fullscreen, getAudioContext, get_messages as getMessages, getObject, getDevice as getTrackSettings, chrome_browser_detect as isChrome, electron_detect as isElectron, mobile_browser_detect as isMobile, safari_browser_detect as isSafari, isTouchable as isTouchscreen, touchableMac as isTouchscreenMac, is_visible as isVisible, load_script as loadScript, mark, matchUA, polyfill as mediaDevices, observable, parseVersion, replace, rtc_supported as rtcSupported, seriesADetect, set_focus as setFocus, test_media as testMedia, test_storage as testStorage, throttle, uuid, value, verify_media as verifyMedia, version };
